import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./style.module.scss";

interface EllipsisTextProps {
  row?: number;
  text: React.ReactNode;
  tooltipText?: React.ReactNode;
  showTip?: boolean;
  className?: string;
  adaptWidth?: boolean;
  isHtml?: boolean;
  style?: React.CSSProperties;
}

const EllipsisText: React.FC<EllipsisTextProps> = ({
  row = 1,
  text,
  tooltipText,
  showTip = true,
  className,
  adaptWidth = false,
  isHtml = false,
  style: customStyle,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } =
          textRef.current;
        const isHorizontalOverflow = scrollWidth > clientWidth;
        const isVerticalOverflow = scrollHeight > clientHeight;
        setIsOverflow(isHorizontalOverflow || isVerticalOverflow);
      }
    };

    const timer = setTimeout(checkOverflow, 0);

    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text]);

  const handleMouseEnter = () => {
    if (isOverflow && showTip && textRef.current) {
      const rect = textRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left,
        y: rect.top - 8, // 悬浮框显示在元素上方
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const baseStyle: React.CSSProperties = {
    width: adaptWidth ? "auto" : "100%",
    overflow: "hidden",
    ...customStyle,
  };

  const textStyle: React.CSSProperties =
    row === 1
      ? {
          ...baseStyle,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }
      : {
          ...baseStyle,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical" as const,
          WebkitLineClamp: row,
          textOverflow: "ellipsis",
        };

  return (
    <>
      <div className={styles.ellipsisWrapper}>
        <div
          ref={textRef}
          style={textStyle}
          className={`${styles.ellipsisText} ${className || ""}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isHtml && typeof text === "string" ? (
            <span dangerouslySetInnerHTML={{ __html: text }} />
          ) : (
            text
          )}
        </div>
      </div>
      {showTooltip &&
        showTip &&
        isOverflow &&
        createPortal(
          <div
            className={`${styles.tooltip} ${
              adaptWidth ? styles.adaptWidth : ""
            }`}
            style={{
              position: "fixed",
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              transform: "translateY(-100%)",
              width: adaptWidth ? textRef.current?.clientWidth : "auto",
              maxWidth: adaptWidth ? "none" : "300px",
              zIndex: 10000,
            }}
          >
            {isHtml && typeof text === "string" ? (
              <span dangerouslySetInnerHTML={{ __html: text }} />
            ) : (
              (tooltipText ?? text)
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export { EllipsisText };
